import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { BoundingBox } from "@babylonjs/core/Culling/boundingBox";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AssetModelType } from "common/model";
import { MAP_MESH_SIZE } from "features/builder/constants";

/**
 * 拾った地面メッシュがそのアセットの置き場所として有効かを判定する
 * - 地面/川はどの高さにも置ける（川は地面の上にも置ける）
 * - それ以外（キャラ・ゴール・ジェム・かざり）はタイルの上（レイヤー0以外）にしか置けない
 *
 * 「川の上には何も置けない」は createGround が川に天面の地面メッシュを作らないことで
 * 実現している（拾える板が無い＝置けない）ため、この判定には現れない
 *
 * 同じ判定が配置・移動の各所で必要になるため、必ずこの関数を経由すること
 */
export const canPlaceOnGround = (
  groundMesh: AbstractMesh | null | undefined,
  type: AssetModelType | undefined,
): boolean => {
  if (!groundMesh) return false;
  if (type === AssetModelType.RIVER || type === AssetModelType.GROUND) return true;
  return groundMesh.name.split("_").pop() !== "0";
};

/**
 * 軽量な親ノードを作成（TransformNode）
 * 旧 createOuterMesh は Box を作成して非表示にしていたが、
 * TransformNode は頂点・面・マテリアルを持たないため効率的
 */
export const createOuterNode = (scene: Scene, position: Vector3): TransformNode => {
  const outer = new TransformNode("outer", scene);
  outer.position = position.clone();
  return outer;
};

const calcMeshSize = (boundingBox: BoundingBox) => ({
  width: Math.round(boundingBox.maximum.x - boundingBox.minimum.x),
  height: Math.round(boundingBox.maximum.z - boundingBox.minimum.z),
});

export const createGround = (
  scene: Scene,
  boundingBox: BoundingBox,
  type: AssetModelType,
  position: Vector3,
  bottomLayer: string | number,
) => {
  const { width, height } = calcMeshSize(boundingBox);
  const originX = Math.floor(position.x + MAP_MESH_SIZE / 2 - width / 2);
  const originY = Math.floor(position.z + MAP_MESH_SIZE / 2 - height / 2);

  for (let x = 0; x < Math.floor(width > 2 ? width / 2 : MAP_MESH_SIZE / 2); x++) {
    for (let y = 0; y < Math.floor(height > 2 ? height / 2 : MAP_MESH_SIZE / 2); y++) {
      if (type === AssetModelType.GROUND) {
        const ground = MeshBuilder.CreateGround(
          `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
            originY + y * MAP_MESH_SIZE
          }_${Math.round(boundingBox.maximumWorld.y)}`,
          {
            width: MAP_MESH_SIZE,
            height: MAP_MESH_SIZE,
          },
          scene,
        );
        ground.id = "ground";
        ground.material = scene.getMaterialByName("ground_layer");
        ground.position = new Vector3(
          originX + x * MAP_MESH_SIZE,
          boundingBox.maximumWorld.y,
          originY + y * MAP_MESH_SIZE,
        );
        ground.showBoundingBox = true;
      }

      const bottomLayerGround = scene.getMeshByName(
        `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
          originY + y * MAP_MESH_SIZE
        }_${bottomLayer}`,
      );
      if (bottomLayerGround) {
        bottomLayerGround.dispose();
      }
    }
  }
};

export const createBottomGround = (scene: Scene, boundingBox: BoundingBox, position: Vector3) => {
  const { width, height } = calcMeshSize(boundingBox);
  const originX = Math.floor(position.x + MAP_MESH_SIZE / 2 - width / 2);
  const originY = Math.floor(position.z + MAP_MESH_SIZE / 2 - height / 2);
  for (let x = 0; x < Math.floor(width > 2 ? width / 2 : MAP_MESH_SIZE / 2); x++) {
    for (let y = 0; y < Math.floor(height > 2 ? height / 2 : MAP_MESH_SIZE / 2); y++) {
      const topGround = scene.getMeshByName(
        `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
          originY + y * MAP_MESH_SIZE
        }_${Math.round(boundingBox.maximumWorld.y)}`,
      );
      if (topGround) topGround.dispose();

      const bottomGround = MeshBuilder.CreateGround(
        `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
          originY + y * MAP_MESH_SIZE
        }_${Math.round(position.y)}`,
        {
          width: MAP_MESH_SIZE,
          height: MAP_MESH_SIZE,
        },
        scene,
      );
      bottomGround.id = "ground";
      bottomGround.showBoundingBox = true;
      bottomGround.material =
        Math.round(position.y) === 0
          ? scene.getMaterialByName("ground")
          : scene.getMaterialByName("ground_layer");
      bottomGround.position = new Vector3(
        originX + x * MAP_MESH_SIZE,
        position.y,
        originY + y * MAP_MESH_SIZE,
      );
    }
  }
};
